/**
 * Single reader of build-time environment variables.
 *
 * This is the only file in `src/` that should touch `process.env` (apart from
 * the CRA built-ins NODE_ENV / PUBLIC_URL). Everything else imports the named
 * values from `utils/constants.js` or `utils/config.js`.
 *
 * Rules:
 * - Only `REACT_APP_*` variables are injected into the bundle by CRA, so every
 *   name below is given WITHOUT that prefix and it is added here.
 * - Values are baked in at build time; changing them at container runtime has
 *   no effect on the bundle.
 * - Every variable read here must be listed in `.env.example`.
 */
const source = process.env;
const missing = [];

const read = (name, required) => {
  const value = source[`REACT_APP_${name}`];
  if (value === undefined || value === '') {
    if (required) missing.push(`REACT_APP_${name}`);
    return undefined;
  }
  return value;
};

const str = (name, fallback, required = false) => {
  const value = read(name, required);
  return value === undefined ? fallback : value;
};

const num = (name, fallback) => {
  const value = parseFloat(read(name));
  return isNaN(value) ? fallback : value;
};

const int = (name, fallback) => {
  const value = parseInt(read(name), 10);
  return isNaN(value) ? fallback : value;
};

const bool = (name, fallback) => {
  const value = read(name);
  return value === undefined ? fallback : value === 'true';
};

const list = name => (read(name) || '').split(',');

/** Roles allowed for a permission, e.g. roles('ACCESS_REPORTS') reads REACT_APP_ACCESS_REPORTS. */
export const roles = permission => list(permission);

const REQUIRED = true;

const env = {
  // ::: release
  VERSION: str('VERSION', '0.0.0'),
  RELEASE_STAGE: str('RELEASE_STAGE', 'development'),

  // ::: APIs
  API: str('API', 'https://qa-insyt-api.esoko.com/api/v1', REQUIRED),
  SSO_API_URL: str('SSO_API_URL', 'https://qa-sso-api.esoko.com/api/v2', REQUIRED),
  SURVEY_API: str('SURVEY_API', 'http://localhost:3000/api/v1'),
  PRICES_API_URL: str('PRICES_API_URL', 'http://prices-api.qa2.esoko.com:8085/api/v1'),
  METRICS_ENDPOINT: str('METRICS_ENDPOINT', 'https://insyt-metrics-api.esoko.com/web/logs'),
  HIGHCHARTS_EXPORT_URL: str('HIGHCHARTS_EXPORT_URL', 'https://export.highcharts.com/'),

  // ::: sibling apps
  INSYT_APP_URL: str('INSYT_APP_URL', undefined, REQUIRED),
  ANALYTICS_APP_URL: str('ANALYTICS_APP_URL', 'http://localhost:3001', REQUIRED),
  ABIS_APP_URL: str('ABIS_APP_URL', 'http://localhost:3002'),
  PUSH_APP_URL: str('PUSH_APP_URL'),
  PEOPLE_APP_URL: str('PEOPLE_APP_URL'),
  ACCOUNT_APP_URL: str('ACCOUNT_APP_URL', undefined, REQUIRED),
  SSO_WEB_URL: str('SSO_WEB_URL', undefined, REQUIRED),
  WEB_FORMS_URL: str('WEB_FORMS_URL', 'http://localhost:3000', REQUIRED),
  WEBSITE_HOME: str('WEBSITE_HOME', '/en/login'),

  // ::: browser storage
  COOKIES_DOMAIN: str('COOKIES_DOMAIN', 'localhost', REQUIRED),
  STORAGE_PREFIX: str('STORAGE_PREFIX', 'esk_'),

  // ::: third-party keys (browser-side keys only, never server secrets)
  INTERCOM_APP_ID: str('INTERCOM_APP_ID', 'jnpmstartxm2i8o8'),
  GOOGLE_MAP_API: str('GOOGLE_MAP_API', 'AIzaSyAXE1zAeTeg5EVQG4tXoiFO2SY3CwO3sSM'),
  RECAPTCHA_SITE_KEY: str('RECAPTCHA_SITE_KEY', '6Le2HWIUAAAAAA77C0BjqJSkbarZPuT9Gfc_GGBi'),

  // ::: feature flags and tunables
  USE_SURVEY_BILLING: bool('USE_SURVEY_BILLING', false),
  REJECT_WITH_CANNED_MESSAGES: bool('REJECT_WITH_CANNED_MESSAGES', true),
  RESPONSE_TABLE_SIZE: int('RESPONSE_TABLE_SIZE', 70),
  ALBUM_EXTRA_FIELDS_COUNT: int('ALBUM_EXTRA_FIELDS_COUNT', 7),
  TREE_LEVELS: int('TREE_LEVELS', 6),
  PRICES_FORM_UUIDS: list('PRICES_FORM_UUIDS'),
  USD_TO_GHS: num('USD_TO_GHS', 5.4),
  GHS_TO_USD: num('GHS_TO_USD', 5.4),

  // ::: response flag restrictions (comma-separated)
  RESTRICT_RESPONSE_FLAGS: str('RESTRICT_RESPONSE_FLAGS', 'Verified,Draft,Ok,Sweet'),
  RESTRICT_RESPONSE_FLAGS_ROLE: str('RESTRICT_RESPONSE_FLAGS_ROLE', 'dev'),
  RESTRICT_RESPONSE_FLAGS_UPDATE: str('RESTRICT_RESPONSE_FLAGS_UPDATE', 'Great,Sweet'),
};

// A production build with one of these unset is silently pointing at a default
// (often a QA or localhost URL), so make it visible instead of failing quietly.
if (source.NODE_ENV === 'production' && missing.length > 0) {
  console.error(
    `Missing build-time environment variables, using defaults: ${missing.join(', ')}`
  );
}

export default env;
